import { useContext, useEffect, useRef, useState } from "react";
import * as turf from "@turf/turf";
import MapboxDraw from "@mapbox/mapbox-gl-draw";

// components
import MapContext from "../../../services/context/map-context";
import { drawThemeUpload } from "./draw-theme-upload";
import DragRotateMode from "../../map/drag-rotate-mode";

// constants
import {
  DRAWMODE,
  PROPERTY_SOURCE,
  UPLOADED_GEOJSON_FEATURE_ID,
} from "../../../constants/site-settings.constants";

// context
import { useAppContext } from "../../../services/context/app-context";

// utils
import {
  addClipLayer,
  isMapExist,
  removeAllPropertyLabels,
  removeClipLayer,
} from "../../../utils/map-layer";
import { reverseGeocode } from "../../../utils/map-labels";

// style
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import rotatePNG from "../../../assets/img/rotate2.png";


export const DisplayUploadedProperty = ({
  propertyGeoJSON,
  updatePropertyData,
  deleteDrawing,
  setDeleteDrawing,
  uploading,
  /* bearing, */
}) => {
  const { map, mapLoaded } = useContext(MapContext);
  const { setPropertyLocation, showBaseBuildings } = useAppContext();
  const drawRef = useRef(null);
  /* const [isDraggingRotate, setIsDraggingRotate] = useState(false); */

  useEffect(() => {
    if (!map || !mapLoaded) return;

    map.loadImage(rotatePNG, (err, img) => {
      if (!err && !map.hasImage("rotate-handle")) {
        map.addImage("rotate-handle", img);
      }
    });

    // Initialize MapboxDraw
    if (!drawRef.current) {
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        defaultMode: DRAWMODE.simple_select,
        modes: { ...MapboxDraw.modes, drag_rotate_handle: DragRotateMode },
        styles: [...drawThemeUpload],
      });
      map.addControl(drawRef.current);

      if (
        propertyGeoJSON &&
        propertyGeoJSON.features[0].id === UPLOADED_GEOJSON_FEATURE_ID
      ) {
        displayProperty();
      }

      /* map.on("draw.rotateStart", () => setIsDraggingRotate(true));
      map.on("draw.rotateEnd",   () => setIsDraggingRotate(false)); */
      map.on("draw.create", updateProperty);
      map.on("draw.update", updateProperty);
      map.on("draw.delete", clearProperty);
    }

    // Cleanup on unmount
    return () => {
      if (map && mapLoaded && drawRef.current && isMapExist(map)) {
        if (typeof map.off === "function") {
          map.off("draw.create", updateProperty);
          map.off("draw.update", updateProperty);
          map.off("draw.delete", clearProperty);
          /* map.off("draw.rotateStart");
          map.off("draw.rotateEnd"); */
        }

        if (map.hasImage("rotate-handle")) {
          map.removeImage("rotate-handle");
        }

        removeClipLayer(map);

        map.getCanvas().style.cursor = "";
        map.removeControl(drawRef.current);
        drawRef.current = null;
      }
    };
  }, [map, mapLoaded]);

  /*  useEffect(() => {
    if (!drawRef.current || isDraggingRotate) return;
    rotateFeatureTo(drawRef.current, map, UPLOADED_GEOJSON_FEATURE_ID, bearing);
  }, [bearing]); */

  // clear existing drawing
  useEffect(() => {
    if (deleteDrawing) {
      clearProperty();
    }
  }, [deleteDrawing]);

    // hide base buildings
  useEffect(() => {
    if (!showBaseBuildings && drawRef.current && propertyGeoJSON) {
      addClipLayer(map, propertyGeoJSON);
    }
    if (showBaseBuildings) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings]);

  const updateProperty = async (e) => {
    const drawFeatures = drawRef.current.getAll();
    const polygon = e.features[0];
    const geoJSON = { type: "FeatureCollection", features: [polygon] };

    if (!polygon) return;

    const property = {
      ...geoJSON,
      features: geoJSON.features.filter(
        (f) => f.id === UPLOADED_GEOJSON_FEATURE_ID
      ),
    };

    // Update the context or local state
    updatePropertyData("uploadedPropertyGeoJSON", property);
    if (!uploading) {
      updatePropertyData("propertyGeoJSON", property);
      const centerPolygon = turf.center(property);
      const mainCoordinates = centerPolygon
        ? {
            lat: centerPolygon.geometry.coordinates[1],
            long: centerPolygon.geometry.coordinates[0],
          }
        : null;

      const address = await reverseGeocode(centerPolygon.geometry.coordinates);

      updatePropertyData("coordinatesCenter", mainCoordinates);
      updatePropertyData("location", address ? address : null);
      setPropertyLocation(
        centerPolygon ? centerPolygon.geometry.coordinates : null
      );
    }

    /* if(e.action === 'rotate' && e.angle){
      updatePropertyData("bearing", e.bearing);
      updatePropertyData("angle", e.angle);
    } */

    // Add a clip layer (eraser) for the polygon
    if(!showBaseBuildings){
      addClipLayer(map, drawFeatures);
    }
    
  };

  const displayProperty = () => {
    // Add the existing propertyGeoJSON
    if (!drawRef.current || !propertyGeoJSON) return;

    const property =
      propertyGeoJSON.type === "FeatureCollection"
        ? {
            ...propertyGeoJSON,
            features: propertyGeoJSON.features.filter(
              (f) => f.id === UPLOADED_GEOJSON_FEATURE_ID
            ),
          }
        : propertyGeoJSON;

    drawRef.current.add(property);
    const featureId = propertyGeoJSON.features[0].id;

    // Switch mode to direct_select for the existing polygon
    if (featureId)
      drawRef.current.changeMode("drag_rotate_handle", {
        featureId: featureId,
      });

    map.getCanvas().style.cursor = "";

    // Add eraser
    if(!showBaseBuildings){
      addClipLayer(map, propertyGeoJSON);
    }
    
  };

  const clearProperty = () => {
    if (!drawRef.current) return;
    drawRef.current.deleteAll();
    /* drawRef.current.changeMode(drawMode); */ // Reset to the same mode
    map.getCanvas().style.cursor = "crosshair";

    // Remove any label layers and sources
    removeAllPropertyLabels(map);
    removeClipLayer(map);

    // Clear property data
    updatePropertyData("propertyGeoJSON", null);
    updatePropertyData("propertyArea", null);
    updatePropertyData("area", null);
    updatePropertyData("perimeter", null);

    updatePropertyData("fileId", null);
    updatePropertyData("fileName", null);
    updatePropertyData("units", null);
    /* updatePropertyData("pivot", null); */
    updatePropertyData("center", null);
   /*  updatePropertyData("bearing", 0); */

    setDeleteDrawing(false);
  };

  return null;
};
